/**
 * DeleteConfirmDialog Component
 * Specialized confirmation dialog for delete operations
 * Reusable across all delete operations: tasks, sprints, etc.
 */
import { ConfirmDialog } from '@/components/ConfirmDialog';

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  itemName: string; // Name of the item being deleted (e.g., task title, sprint name)
  itemType?: string; // Type of item (e.g., "task", "sprint", "project") - for custom messages
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  customMessage?: string; // Optional custom message, otherwise generates from itemName
}

/**
 * DeleteConfirmDialog - A specialized confirmation dialog for delete operations
 * 
 * @example
 * // In your component:
 * const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
 * const [isDeleting, setIsDeleting] = useState(false);
 * 
 * <DeleteConfirmDialog
 *   isOpen={showDeleteConfirm}
 *   itemName={task.title}
 *   itemType="task"
 *   onConfirm={async () => {
 *     setIsDeleting(true);
 *     try {
 *       await deleteTask(task.id);
 *     } finally {
 *       setIsDeleting(false);
 *       setShowDeleteConfirm(false);
 *     }
 *   }}
 *   onCancel={() => setShowDeleteConfirm(false)}
 *   isLoading={isDeleting}
 * />
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  isOpen,
  itemName,
  itemType = "item",
  onConfirm,
  onCancel,
  isLoading = false,
  customMessage,
}) => {
  const title = `Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}?`;
  
  const defaultMessage = `Are you sure you want to delete "${itemName}"?.`;
  const message = customMessage || defaultMessage;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={title}
      message={message}
      confirmText="Delete"
      cancelText="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
      icon="delete"
      isDangerous={true}
    />
  );
};
